import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Sidebar from '@/components/Sidebar';
import {
  Bot,
  Plus,
  BarChart3,
  MessageSquare,
  Users,
  Settings,
  ShoppingCart,
  Headphones,
  TrendingUp,
  Loader2,
  Menu
} from 'lucide-react';

const AgentsPage = () => {
  const { token } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);

  const [agents, setAgents] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [newBotDescription, setNewBotDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chatbots`, { headers, withCredentials: true });
      const agentList = res.data || [];
      setAgents(agentList);

      const analyticsPromises = agentList.map(agent =>
        axios.get(`${API}/analytics/${agent.id}`, { headers, withCredentials: true })
          .then(r => ({ id: agent.id, data: r.data }))
          .catch(() => ({ id: agent.id, data: null }))
      );
      const results = await Promise.all(analyticsPromises);
      const analyticsMap = {};
      results.forEach(r => { analyticsMap[r.id] = r.data; });
      setAnalytics(analyticsMap);
    } catch (error) {
      toast.error(language === 'id' ? 'Gagal memuat data agent' : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const createAgent = async () => {
    if (!newBotName.trim()) {
      toast.error(language === 'id' ? 'Nama agent harus diisi' : 'Agent name is required');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/chatbots`, {
        name: newBotName,
        description: newBotDescription
      }, { headers, withCredentials: true });
      setCreateDialogOpen(false);
      setNewBotName('');
      setNewBotDescription('');
      toast.success('AI Agent berhasil dibuat!');
      navigate(`/agent/${response.data.id}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal membuat agent';
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const getTypeConfig = (type) => {
    if (type === 'cs') return {
      short: 'CS',
      icon: Headphones,
      badgeBg: 'bg-indigo-100 text-indigo-700 dark:bg-blue-900/40 dark:text-indigo-300'
    };
    return {
      short: 'Sales',
      icon: ShoppingCart,
      badgeBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    };
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] transition-colors">
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
        
        <div className="lg:ml-64" data-testid="agents-page">
          <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
            <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-600 dark:text-zinc-300" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
                    AI Agents
                  </h1>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mt-0.5">
                    {language === 'id' ? 'Kelola semua AI agent toko Anda' : 'Manage all your store AI agents'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                data-testid="create-agent-btn"
              >
                <Plus className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">{language === 'id' ? 'Buat Agent' : 'Create Agent'}</span>
              </Button>
            </div>
          </header>

          <main className="max-w-5xl mx-auto px-4 py-6">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-pulse" data-testid="agents-skeleton">
                {[0, 1, 2, 3].map(i => (
                  <div key={i} className="h-40 rounded-xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800" />
                ))}
              </div>
            ) : agents.length === 0 ? (
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-12 text-center" data-testid="agents-empty-state">
                <div className="w-16 h-16 rounded-xl bg-slate-100 dark:bg-zinc-800 flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-slate-500 dark:text-zinc-400" />
                </div>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2">
                  {language === 'id' ? 'Belum ada AI Agent' : 'No AI Agents yet'}
                </h2>
                <p className="text-sm text-slate-500 dark:text-zinc-400 mb-6 max-w-sm mx-auto">
                  {language === 'id'
                    ? 'Buat AI agent pertama Anda untuk mulai melayani customer secara otomatis.'
                    : 'Create your first AI agent to start serving customers automatically.'}
                </p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  {language === 'id' ? 'Buat Agent Pertama' : 'Create First Agent'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {agents.map((agent) => {
                  const typeConfig = getTypeConfig(agent.agent_type);
                  const TypeIcon = typeConfig.icon;
                  const stats = analytics[agent.id];

                  return (
                    <div
                      key={agent.id}
                      className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 overflow-hidden transition-shadow hover:shadow-md"
                      data-testid={`agent-card-${agent.id}`}
                    >
                      <div className="p-5 pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-center gap-3.5 min-w-0">
                            <div
                              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: (agent.primary_color || '#10B981') + '15' }}
                            >
                              <Bot className="w-5 h-5" style={{ color: agent.primary_color || '#10B981' }} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-slate-900 dark:text-zinc-50 truncate">
                                {agent.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Badge className={`h-5 px-1.5 text-[10px] font-medium border-0 ${typeConfig.badgeBg}`}>
                                  <TypeIcon className="w-3 h-3 mr-0.5" />
                                  {typeConfig.short}
                                </Badge>
                                <span className="text-xs text-slate-500 dark:text-zinc-500">
                                  {agent.language === 'en' ? 'English' : 'Indonesia'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2.5 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                              onClick={() => navigate(`/analytics/${agent.id}`)}
                              data-testid={`agent-analytics-btn-${agent.id}`}
                            >
                              <BarChart3 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2.5 text-slate-500 hover:text-slate-900 dark:text-zinc-400 dark:hover:text-white"
                              onClick={() => navigate(`/agent/${agent.id}`)}
                              data-testid={`agent-settings-btn-${agent.id}`}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="px-5 pb-5">
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-slate-50 dark:bg-[#0A0A0A]/50 rounded-xl p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <Users className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                              <span className="text-[11px] text-slate-500 dark:text-zinc-400">
                                {language === 'id' ? 'Percakapan' : 'Conversations'}
                              </span>
                            </div>
                            <p className="text-lg font-bold text-slate-900 dark:text-zinc-50" data-testid={`agent-conv-count-${agent.id}`}>
                              {stats ? (stats.total_conversations || 0).toLocaleString() : '-'}
                            </p>
                          </div>
                          <div className="bg-slate-50 dark:bg-[#0A0A0A]/50 rounded-xl p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <MessageSquare className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                              <span className="text-[11px] text-slate-500 dark:text-zinc-400">
                                {language === 'id' ? 'Pesan' : 'Messages'}
                              </span>
                            </div>
                            <p className="text-lg font-bold text-slate-900 dark:text-zinc-50" data-testid={`agent-msg-count-${agent.id}`}>
                              {stats ? (stats.total_chats || 0).toLocaleString() : '-'}
                            </p>
                          </div>
                          <div className="bg-slate-50 dark:bg-[#0A0A0A]/50 rounded-xl p-3">
                            <div className="flex items-center gap-1.5 mb-1">
                              <TrendingUp className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
                              <span className="text-[11px] text-slate-500 dark:text-zinc-400">
                                {language === 'id' ? 'Kepuasan' : 'Satisfaction'}
                              </span>
                            </div>
                            <p className="text-lg font-bold text-slate-900 dark:text-zinc-50">
                              {stats ? (() => {
                                const total = (stats.sentiment?.positive || 0) + (stats.sentiment?.neutral || 0) + (stats.sentiment?.negative || 0);
                                return total > 0 ? Math.round((stats.sentiment.positive / total) * 100) + '%' : '-';
                              })() : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </main>
        </div>

        {/* Create Agent Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>{language === 'id' ? 'Buat AI Agent Baru' : 'Create New AI Agent'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="agent-name">{language === 'id' ? 'Nama Agent' : 'Agent Name'}</Label>
                <Input
                  id="agent-name"
                  value={newBotName}
                  onChange={(e) => setNewBotName(e.target.value)}
                  placeholder="Contoh: CS Toko Online"
                  onKeyDown={(e) => e.key === 'Enter' && createAgent()}
                  data-testid="create-agent-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="agent-desc">{language === 'id' ? 'Deskripsi (opsional)' : 'Description (optional)'}</Label>
                <Textarea
                  id="agent-desc"
                  value={newBotDescription}
                  onChange={(e) => setNewBotDescription(e.target.value)}
                  placeholder={language === 'id' ? 'Deskripsi singkat' : 'Short description'}
                  data-testid="create-agent-desc-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} data-testid="create-agent-cancel-btn">
                {language === 'id' ? 'Batal' : 'Cancel'}
              </Button>
              <Button
                onClick={createAgent}
                disabled={creating}
                className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                data-testid="create-agent-submit-btn"
              >
                {creating ? 'Membuat...' : (language === 'id' ? 'Buat' : 'Create')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AgentsPage;
